/***
 * Created on 2024/3/26
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useEffect, useState} from 'react';
import Captcha from "../common/captcha";
import ic_sys_info_svg from "../../static/img/utils/ic-sys-info.svg";
import {Tooltip} from "react-tooltip";
import {useTranslation} from "react-i18next";
import {useOnChange} from "common/Hook";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {apiGet, apiPost} from "../../service/api";
import {getErrorMessage} from 'common/ServiceCommon';
import {getLocalUser, getPrimaryEmail} from "common/ServiceUser";
import {useCountdown} from "../../hook/countdown";
import {useBoundedStore} from 'common/Store';


function SmsEmailVerificationComponent({method, twoStepInfo, isObserver, onLaunchQnapAuth, sendTwoStepCode, signout, verifyTwoStepMutate, otherWay, noCode}) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [errorMessage, setErrorMessage] = useState();
    const cur_url = new URL(location.href);
    const {seconds, setSeconds} = useCountdown(0);
    const local_user = getLocalUser();
    const email = getPrimaryEmail(local_user);
    const {values, setValues, onChange} = useOnChange({
        code: "",
        trust_device: true
    });
    const store = useBoundedStore();

    const {data, isSuccess} = useQuery({
        queryKey: ['get_countdown'],
        queryFn: async () => await apiGet('/ajax/countdown')
    });

    const verifyTwoStep = async () => {
        store.globalAlert.setAlert('', 'error', false);
        const data = {
            ...values,
            verify_type: method
        }
        setErrorMessage(null);
        await verifyTwoStepMutate.mutateAsync(data);
    }

    useEffect(() => {
        if (data && isSuccess) {
            const now = new Date().getTime();
            const value = Math.round((now - data?.countdown) / 1000) || 0;
            setSeconds(value > 60 ? 0 : 60 - value);
        }
    }, [data, isSuccess]);

    return (
        <>
            <div className="common-page-body">
                <div className="common-info-grid alert-content--info !display-style--info mb-24 hide">
                    <div className="common-info-grid__row">
                        {t('@SMS service is currently undergoing maintenance. You can only receive verification code through email now.@')}
                    </div>
                </div>

                {['sms_1', 'sms_2'].includes(method) && <p>{t('@Enter the verification code sent to your phone number@')} <span className="font-semibold pl-4 pr-4">{twoStepInfo?.data?.[method]?.phone_number}</span></p>}
                {method === 'google_auth' && <p>{t('@Enter the verification code generated by your authenticator app@')}</p>}
                {method === 'email' && <p>{t('@Send a verification code to your QID email account@')} <span className="font-semibold pl-4 pr-4">{email}</span></p>}

                {
                    cur_url.searchParams.get('qauth_totp')?.toLowerCase() === "true" && method !== 'email' && !['sms_1', 'sms_2'].includes(method) &&
                    <p>
                        <button className="q-btn q-btn--default btn-rounded-full btn-tiny" onClick={onLaunchQnapAuth}>{t('@Launch QNAP Authenticator@')}</button>
                    </p>
                }

                <div className="common-form-group">
                    <div className="comp-input">
                        <div className="input-field--hybrid">
                            <input type="text" className="input--must-fill" name="code" value={values.code} onChange={onChange} pattern="[0-9]+" />
                            { method !== 'google_auth' &&
                                <button className="q-btn q-btn-default" disabled={seconds > 0} onClick={() => sendTwoStepCode(method)}>
                                    {t('@Send@')} {seconds > 0 ? seconds : ''}
                                </button>
                            }
                        </div>
                    </div>
                    {errorMessage && <p className="text-error mt-10">{errorMessage}</p>}
                </div>

                <Captcha value={values.captcha} inputChangeHandler={onChange} resetValue={() => setValues(prev => ({...prev, captcha: ''}))}/>

                {!isObserver &&
                    <div className="comp-checkbox mb-16">
                        <label className="q-checkbox">
                            <input type="checkbox" name="trust_device" checked={values.trust_device}
                                   onChange={() => setValues(prev => ({
                                       ...prev,
                                       trust_device: !prev.trust_device
                                   }))}/>
                            <div className="display-inline-flex align-items-center">
                                <span className="display-inline-block mr-8">{t('@Trust this computer@')}</span>
                                <img src={ic_sys_info_svg} className="w-18px h-18px"
                                     data-tooltip-id="trust-device"
                                     data-tooltip-content={t('@In case you lose your phone, you might be able to access your account from a trusted computer without needing a verification code.@')}/>
                                <Tooltip id="trust-device"/>
                            </div>
                        </label>
                    </div>
                }

                <p className="!mb-10"><a href='' onClick={otherWay}>{t('@Other ways to get a verification code@')}</a></p>
                <p className="!mb-0"><a href="" onClick={noCode}>{t("@Did not receive a code?@")}</a></p>
            </div>
            <div className="common-page-footer">
                <div className="q-btn-group btn-align-right">
                    <button className="q-btn q-btn--primary btn-medium btn-rounded-normal" onClick={verifyTwoStep} disabled={values?.code?.length <= 0}>{t('@Verify@')}</button>
                    <button className="q-btn q-btn--default btn-medium btn-rounded-normal" onClick={signout}>{t('@Back@')}</button>
                </div>
            </div>
        </>
    );
}

export default SmsEmailVerificationComponent;